import React from 'react';
import styled, { css } from 'styled-components';
import { useTable, useSortBy, useFlexLayout } from 'react-table';

import { TableColumnDefinitions } from '~/components/DataTables/types.flow';
import { InvoiceListData } from '../util/composeInvoiceListData';

import {
  Row,
  Body,
  Header,
  DataTableContainer,
} from '~/components/DataTables/styling';
import { HeaderCell, RowCell } from '~/components/DataTables';
import { isInitialLoadStatus } from '~/graphql/ApolloConstants';
import { NoResultSection, Loading } from '~/components';
import Catalog from '~/Catalog';

import TEST_ID from './InvoiceListTable.testid';

const text = {
  noSearchResults: Catalog.noResults,
};
type Props = {
  columns: TableColumnDefinitions;
  data: Array<InvoiceListData>;
  networkStatus: number;
  loading: boolean;
};

const InvoiceListTable = ({
  columns,
  data,
  networkStatus,
  loading,
  ...rest
}: Props) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
        defaultColumn,
        disableMultiSort: true,
        autoResetSortBy: false,
        autoResetHiddenColumns: false,
        getRowId: row => row.id,
        manualSortBy: true,
        initialState: {
          sortBy: [{ id: 'createdDate', desc: true }],
        },
      },
      useFlexLayout,
      useSortBy,
    );

  let bodyComponent;
  if (isInitialLoadStatus(networkStatus) || loading) {
    bodyComponent = <Loading />;
  } else if (data.length === 0) {
    bodyComponent = (
      <StyledNoResultSection>{text.noSearchResults}</StyledNoResultSection>
    );
  } else {
    bodyComponent = (
      <Body.Standard.Inner {...getTableBodyProps()}>
        {rows.map(row => {
          prepareRow(row);

          const rowProps = { ...row.getRowProps() };
          const invoiceId = row.id;
          const isDeleted = row.original && row.original.isDeleted === true;

          return (
            <Row.Standard
              {...rowProps}
              $isSelected={row.isSelected}
              $isDeleted={isDeleted}
              key={rowProps.key}
              data-testid={TEST_ID.LIST_ITEM}
              data-objectid={invoiceId}
              data-deleted={isDeleted}
            >
              {row.cells.map((cell, idx) => {
                const isLastColumn = idx === row.cells.length - 1;

                return (
                  <RowCell
                    key={`cell-${idx}`}
                    cell={cell}
                    isLastColumn={isLastColumn}
                  />
                );
              })}
            </Row.Standard>
          );
        })}
      </Body.Standard.Inner>
    );
  }

  return (
    <TableContainer>
      <DataTableContainer.Standard
        {...rest}
        {...getTableProps()}
        data-testid={TEST_ID.CONTAINER}
      >
        <div>
          {headerGroups.map(headerGroup => {
            const headerGroupProps = {
              ...headerGroup.getHeaderGroupProps(),
            };
            return (
              <Header.Standard key={headerGroupProps.key}>
                {headerGroup.headers.map((column, idx) => {
                  const isLastColumn = idx === headerGroup.headers.length - 1;
                  const headerCellKey = `header-${idx}`;

                  return (
                    <StyledHeaderCell
                      key={headerCellKey}
                      column={column}
                      isLastColumn={isLastColumn}
                    />
                  );
                })}
              </Header.Standard>
            );
          })}
        </div>
        <Body.Standard.Outer>{bodyComponent}</Body.Standard.Outer>
      </DataTableContainer.Standard>
    </TableContainer>
  );
};

const TableContainer = styled(DataTableContainer.StandardOuter)(
  ({ theme }) => css`
    box-shadow: ${theme.getTokens().boxShadow.base};
    padding: ${theme.space('s')};
  `,
);

const StyledHeaderCell = styled(HeaderCell)<{}>(
  ({ theme }) => css`
    padding-bottom: ${theme.space('l')};
  `,
);

const defaultColumn = {
  minWidth: 20,
  width: 120,
  maxWidth: 200,
};

const StyledNoResultSection = styled(NoResultSection)<{}>`
  max-width: 800px;
`;

export default InvoiceListTable;
